import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Box, useMediaQuery, IconButton, Card, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import React, { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router'

/**
 * SimpleHorizontalCards - 一个在小屏幕上支持水平滚动的卡片布局组件
 * @param {Object} props
 * @param {React.ReactNode[]} props.children - 要显示的卡片内容
 * @param {Object} [props.sx] - 传递给容器的样式
 * @param {Object} [props.cardSx] - 传递给每个卡片容器的样式
 * @param {string} [props.breakpoint='sm'] - 响应式断点，默认为 'sm'
 * @returns {JSX.Element}
 */
const SimpleHorizontalCards = ({ children, sx = {}, cardSx = {}, onShowAll = null }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const scrollContainerRef = useRef(null)
  const [showLeftButton, setShowLeftButton] = useState(false)
  const [showRightButton, setShowRightButton] = useState(true)

  // 响应式断点检测
  // isSmallScreen: 小于 600px 的屏幕 (手机)
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  // isMediumScreen: 小于 900px 的屏幕 (平板)
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'))
  // isLargeScreen: 小于 1200px 的屏幕 (小型笔记本)
  const isLargeScreen = useMediaQuery(theme.breakpoints.down('lg'))

  const handleScroll = () => {
    const container = scrollContainerRef.current
    if (container) {
      setShowLeftButton(container.scrollLeft > 0)
      setShowRightButton(container.scrollLeft < container.scrollWidth - container.clientWidth - 10)
    }
  }

  useEffect(() => {
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)
      // Initial check
      handleScroll()
      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const scroll = (direction) => {
    const container = scrollContainerRef.current
    if (container) {
      const scrollAmount = direction === 'left' ? -400 : 400
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' })
    }
  }

  return (
    <Box sx={{ position: 'relative', ...sx }}>
      {showLeftButton && !isSmallScreen && (
        <IconButton
          sx={{
            position: 'absolute',
            left: -20,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': { bgcolor: 'background.paper' },
          }}
          onClick={() => scroll('left')}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}

      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          pb: 2,
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          scrollBehavior: 'smooth',
        }}
      >
        {React.Children.map(children, (child, index) => (
          <Box
            key={index}
            sx={{
              minWidth: { xs: '85vw', sm: '400px' },
              pr: 2,
              display: 'inline-block',
              ...cardSx,
            }}
          >
            {child}
          </Box>
        ))}
        {isMediumScreen && onShowAll && children.length > 1 && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'stretch',
              justifyContent: 'center',
              minWidth: { xs: '160px', sm: '140px' },
              pr: 2,
            }}
          >
            <Card
              onClick={onShowAll}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                px: 2,
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                  }}
                >
                  Show All
                </Typography>
                <ArrowForwardIcon
                  sx={{
                    color: 'primary.main',
                    fontSize: '1.2rem',
                  }}
                />
              </Box>
            </Card>
          </Box>
        )}
      </Box>

      {showRightButton && !isSmallScreen && (
        <IconButton
          sx={{
            position: 'absolute',
            right: -20,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': { bgcolor: 'background.paper' },
          }}
          onClick={() => scroll('right')}
        >
          <ChevronRightIcon />
        </IconButton>
      )}
    </Box>
  )
}

export default SimpleHorizontalCards
